import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Loader2, Check } from 'lucide-react';

const RoleSelection = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hoveredRole, setHoveredRole] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const roles = [
    {
      id: 'doctor',
      title: 'Doctor',
      icon: '🩺',
      description: 'MBBS, MD, Specialists, Surgeons, Interns',
      color: '#2196f3',
      gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      lightBg: '#e3f2fd'
    },
    {
      id: 'patient',
      title: 'Patient',
      icon: '👤',
      description: 'Individuals managing personal or family health',
      color: '#4caf50',
      gradient: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
      lightBg: '#e8f5e9'
    },
    {
      id: 'researcher',
      title: 'Researcher',
      icon: '🔬',
      description: 'Medical researchers, PhD scholars, public health analysts',
      color: '#9c27b0',
      gradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
      lightBg: '#f3e5f5'
    },
    {
      id: 'clinic',
      title: 'Clinic / Hospital',
      icon: '🏥',
      description: 'Hospitals, clinics, diagnostic centers, institutions',
      color: '#ff9800',
      gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      lightBg: '#fff3e0'
    }
  ];

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
  };

  const handleContinue = async () => {
    if (!selectedRole) return;

    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/user/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: selectedRole })
      });

      if (response.ok) {
        // Store role locally for quick access
        localStorage.setItem('medplexity_user_role', selectedRole);
        
        // Map role to mode for existing mode-based features
        const modeMapping = {
          'doctor': 'clinic',
          'patient': 'patient',
          'researcher': 'researcher',
          'clinic': 'clinic'
        };
        localStorage.setItem('medplexity_mode', modeMapping[selectedRole] || 'patient');
        
        // Navigate directly to unified dashboard
        navigate('/dashboard', { replace: true });
      } else {
        console.error('Failed to save role');
        localStorage.setItem('medplexity_user_role', selectedRole);
        localStorage.setItem('medplexity_mode', selectedRole === 'patient' ? 'patient' : selectedRole === 'researcher' ? 'researcher' : 'clinic');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Error saving role:', error);
      localStorage.setItem('medplexity_user_role', selectedRole);
      localStorage.setItem('medplexity_mode', selectedRole === 'patient' ? 'patient' : selectedRole === 'researcher' ? 'researcher' : 'clinic');
      navigate('/dashboard', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '900px',
        width: '100%',
        background: 'white',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
        padding: '3rem',
        textAlign: 'center'
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          marginBottom: '2rem'
        }}>
          <Activity size={40} color="#0277bd" />
          <span style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#0277bd'
          }}>
            MedPlexity
          </span>
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 700,
          color: '#1a1a2e',
          marginBottom: '0.5rem'
        }}>
          🔐 How will you use MedPlexity?
        </h1>
        <p style={{
          color: '#64748b',
          fontSize: '1.1rem',
          marginBottom: '2.5rem'
        }}>
          This helps us personalize your experience
        </p>

        {/* Role Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2.5rem'
        }}>
          {roles.map((role) => {
            const isSelected = selectedRole === role.id;
            const isHovered = hoveredRole === role.id;

            return (
              <div
                key={role.id}
                onClick={() => handleRoleSelect(role.id)}
                onMouseEnter={() => setHoveredRole(role.id)}
                onMouseLeave={() => setHoveredRole(null)}
                style={{
                  position: 'relative',
                  padding: '2rem 1.5rem',
                  borderRadius: '16px',
                  border: isSelected 
                    ? `3px solid ${role.color}` 
                    : '3px solid transparent',
                  background: isSelected ? role.lightBg : isHovered ? '#f8fafc' : 'white',
                  boxShadow: isSelected 
                    ? `0 8px 24px ${role.color}30`
                    : isHovered 
                      ? '0 8px 24px rgba(0,0,0,0.1)'
                      : '0 4px 12px rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: isSelected || isHovered ? 'translateY(-4px)' : 'translateY(0)'
                }}
              >
                {/* Selection Check */}
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: role.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }}>
                    <Check size={18} color="white" strokeWidth={3} />
                  </div>
                )}

                {/* Icon */}
                <div style={{
                  fontSize: '4rem',
                  marginBottom: '1rem',
                  filter: isSelected ? 'none' : 'grayscale(20%)',
                  transition: 'filter 0.3s'
                }}>
                  {role.icon}
                </div>

                {/* Title */}
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: isSelected ? role.color : '#1a1a2e',
                  marginBottom: '0.5rem',
                  transition: 'color 0.3s'
                }}>
                  {role.title}
                </h3>

                {/* Description */}
                <p style={{
                  fontSize: '0.9rem',
                  color: '#64748b',
                  lineHeight: 1.5,
                  margin: 0
                }}>
                  {role.description}
                </p>

                {/* Subtle indicator bar */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isSelected ? '60%' : '0%',
                  height: '4px',
                  background: role.gradient,
                  borderRadius: '4px 4px 0 0',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={!selectedRole || saving}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            padding: '1rem 3rem',
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'white',
            background: selectedRole 
              ? 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)'
              : '#cbd5e1',
            border: 'none',
            borderRadius: '12px',
            cursor: selectedRole && !saving ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s ease',
            boxShadow: selectedRole 
              ? '0 8px 24px rgba(2, 119, 189, 0.3)'
              : 'none',
            minWidth: '200px'
          }}
          onMouseEnter={(e) => {
            if (selectedRole && !saving) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(2, 119, 189, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = selectedRole 
              ? '0 8px 24px rgba(2, 119, 189, 0.3)'
              : 'none';
          }}
        >
          {saving ? (
            <>
              <Loader2 size={20} className="spinning" />
              Setting up...
            </>
          ) : (
            <>
              Continue to MedPlexity
              <span style={{ fontSize: '1.25rem' }}>→</span>
            </>
          )}
        </button>

        {/* Footer note */}
        <p style={{
          marginTop: '1.5rem',
          fontSize: '0.85rem',
          color: '#94a3b8'
        }}>
          You can change this later from your profile settings
        </p>
      </div>
    </div>
  );
};

export default RoleSelection;
